import { Injectable, inject, resource } from "@angular/core";
import { firstValueFrom, filter, take } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";

@Injectable()
export abstract class IFirmService<D> {
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    protected abstract getMessage: string;
    protected abstract gotMessage: string;

    protected itemsResource = resource<D[], undefined>({
        defaultValue: [],
        loader: () => this.load(),
    });
    public readonly items = this.itemsResource.value.asReadonly();
    private readonly items$ = toObservable(this.items);

    protected async load(params: object = {}): Promise<any> {
        try {
            const result = await this.iframeMessagesService.postMessageResp(this.getMessage, params, this.gotMessage);
            if (result.status === "success") return result;
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    public async getItems(): Promise<D[]> {
        await this.waitForItems();
        return this.items();
    }

    // Do not use it. Only use in getItems() for fallback compatibility. It will be removed in the future.
    private async waitForItems(): Promise<void> {
        await firstValueFrom(
            this.items$.pipe(
                filter((items) => items !== undefined && items !== null),
                take(1),
            ),
        );
    }
}
